import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { Location } from '../../../models/location/Location.model';
import { DunaProfileImageComponent } from '../duna-profile-image/duna-profile-image.component';
import { DunaUserProgressLabelComponent } from '../duna-user-progress-label/duna-user-progress-label.component';
import {
  DirectorySortColumn,
  DirectorySortDirection,
  buildServerLoadParams,
  mapLocationListSortToApi,
  toggleDirectorySort,
} from '../duna-directory-list/duna-directory-list.logic';

interface LocationListRow {
  location: Location;
  guid: string;
  initials: string;
  profileImageObfuscatedName: string | null;
  locationId: number | undefined;
  firefighterCount: number;
  okPercentage: number;
  registeredPercentage: number;
  progressPercentage: number;
  completedBarWidth: number;
  intendedBarWidth: number;
}

@Component({
  selector: 'app-duna-list-locations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    MatPaginatorModule,
    MatTooltipModule,
    DunaProfileImageComponent,
    DunaUserProgressLabelComponent,
  ],
  templateUrl: './duna-list-locations.component.html',
  styleUrl: './duna-list-locations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaListLocationsComponent implements OnInit, OnDestroy {
  readonly pageSizeOptions = [10, 25, 50, 100];

  locationSearch = '';
  loading = false;
  loadError: string | null = null;

  pageIndex = 0;
  pageSize = 25;
  totalCount = 0;

  sortColumn: DirectorySortColumn = 'identity';
  sortDirection: DirectorySortDirection = 'asc';

  displayedRows: LocationListRow[] = [];

  private readonly destroy$ = new Subject<void>();
  private readonly searchInput$ = new Subject<string>();

  constructor(
    private dataSourceService: ComponentDataSourceService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadLocations();
      });
    this.loadLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLocationSearchChange(value: string): void {
    this.locationSearch = value;
    this.searchInput$.next(value);
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadLocations();
  }

  onSortHeaderClick(column: DirectorySortColumn, event: Event): void {
    event.stopPropagation();
    const next = toggleDirectorySort(
      this.sortColumn,
      this.sortDirection,
      column
    );
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
    this.pageIndex = 0;
    this.loadLocations();
  }

  sortIcon(column: DirectorySortColumn): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getCountLabel(): string {
    return `${this.totalCount}`;
  }

  onLocationRowClick(row: LocationListRow): void {
    if (!row.guid) {
      return;
    }
    this.router.navigate(['/lokation', row.guid]);
  }

  private loadLocations(): void {
    this.loading = true;
    this.loadError = null;

    this.dataSourceService
      .loadDataWithFilters(
        'locations',
        buildServerLoadParams(
          'locations',
          this.pageIndex,
          this.pageSize,
          this.locationSearch,
          this.sortColumn,
          this.sortDirection,
          mapLocationListSortToApi
        )
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = (result?.items ?? []) as Location[];
          this.displayedRows = items.map((location) =>
            this.buildLocationRow(location)
          );
          this.totalCount = result?.totalCount ?? items.length;
          this.pageIndex = result?.pageIndex ?? this.pageIndex;
          this.pageSize = result?.pageSize ?? this.pageSize;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.displayedRows = [];
          this.totalCount = 0;
          this.loading = false;
          this.loadError = 'Kunne ikke indlæse lokationer. Prøv igen senere.';
          this.cdr.markForCheck();
        },
      });
  }

  private buildLocationRow(location: Location): LocationListRow {
    const registeredPercentage = this.clampPercent(
      location.registered_percentage
    );
    const progressPercentage = this.clampPercent(location.progress_percentage);
    const okPercentage = this.clampPercent(location.ok_percentage);
    const firefighterCount = location.firefighter_count ?? 0;
    return {
      location,
      guid: location.guid,
      locationId: location.id,
      initials: this.getLocationInitials(location.locations_name),
      profileImageObfuscatedName: location.profile_image_obfuscated_name ?? null,
      firefighterCount,
      okPercentage,
      registeredPercentage,
      progressPercentage,
      completedBarWidth: progressPercentage,
      intendedBarWidth: Math.max(0, registeredPercentage - progressPercentage),
    };
  }

  private getLocationInitials(name: string): string {
    const trimmed = name?.trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  }

  private clampPercent(value: number | undefined | null): number {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
      return 0;
    }
    return Math.min(100, Math.max(0, Number(value)));
  }
}
